import React, {useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import ErrorToast from '../components/ErrorToast';
import {Check, X} from 'lucide-react';
import {SubscriptionType} from "../models/enums/SubscriptionType";
import {plans} from "../models/SubscriptionPlans";

interface LocationState {
    email?: string;
}

const SubscriptionSelectPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { email } = (location.state as LocationState) || {};

    const [selection, setSelection] = useState<SubscriptionType | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const canSubmit = useMemo(() => !!selection && !submitting, [selection, submitting]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email) {
            setErrorMessage('Missing user email. Please log in again.');
            return;
        }
        if (!selection) return;

        setSubmitting(true);

        if(selection === SubscriptionType.FREE) {
            navigate('/login', {
                state: {
                    message: 'Account created successfully! You can now log in.',
                },
                replace: true,
            });
        } else {
            navigate('/checkout', {
                state: {
                    subscriptionType: selection,
                    email: email
                },
                replace: true,
            });
        }
        setSubmitting(false);
    };

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <NavBar />
            <main className="flex-1">
                <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <header className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-crumbblue">Choose your plan</h1>
                        <p className="text-gray-600 mt-2">Pick the plan that fits how you work. You can switch later.</p>
                    </header>

                    {/* Plan radio cards */}
                    <form onSubmit={handleSubmit} className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {plans.map((p) => {
                                const checked = selection === p.key;
                                return (
                                    <label
                                        data-testid={`${p.name}-card`}
                                        key={p.key}
                                        className={`block cursor-pointer rounded-2xl border p-5 shadow-sm transition ${
                                            checked
                                                ? 'border-crumborange ring-2 ring-crumborange/40'
                                                : 'border-gray-200 hover:border-crumbblue/40'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="radio"
                                                        name="plan"
                                                        className="h-4 w-4 text-crumborange"
                                                        checked={checked}
                                                        onChange={() => setSelection(p.key)}
                                                        aria-label={`Select ${p.name} plan`}
                                                    />
                                                    <span className="text-xl font-semibold">{p.name}</span>
                                                </div>
                                                <div className="text-sm text-gray-600 mt-1">{p.highlights.join(' • ')}</div>
                                            </div>
                                            <div className="text-right">
                                                <span className="inline-block rounded-full px-3 py-1 text-sm font-medium bg-gray-100">{p.priceLabel}</span>
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>

                        {/* Comparison table */}
                        <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Feature</th>
                                    <th scope="col" className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Free</th>
                                    <th scope="col" className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Premium</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                <tr data-testid='row-total-clients'>
                                    <td className="px-6 py-4 text-sm">Total Clients</td>
                                    <td className="px-6 py-4 text-sm text-center">5</td>
                                    <td className="px-6 py-4 text-sm text-center">Unlimited</td>
                                </tr>
                                <tr data-testid='row-interactions'>
                                    <td className="px-6 py-4 text-sm">Record Interactions</td>
                                    <td className="px-6 py-4 text-sm text-center">3 per client</td>
                                    <td className="px-6 py-4 text-sm text-center">Unlimited</td>
                                </tr>
                                <tr data-testid='row-follow-ups'>
                                    <td className="px-6 py-4 text-sm">Set Follow-ups</td>
                                    <td className="px-6 py-4 text-sm text-center">3 per client</td>
                                    <td className="px-6 py-4 text-sm text-center">Unlimited</td>
                                </tr>
                                <tr data-testid='row-contacts'>
                                    <td className="px-6 py-4 text-sm">Add client contacts</td>
                                    <td className="px-6 py-4 text-sm text-center">3 per client</td>
                                    <td className="px-6 py-4 text-sm text-center">Unlimited</td>
                                </tr>
                                <tr data-testid='row-ai'>
                                    <td className="px-6 py-4 text-sm">AI features</td>
                                    <td className="px-6 py-4 text-sm text-center text-red-500"><X className="mx-auto h-5 w-5" /></td>
                                    <td className="px-6 py-4 text-sm text-center text-green-600"><Check className="mx-auto h-5 w-5" /></td>
                                </tr>
                                <tr data-testid='row-files'>
                                    <td className="px-6 py-4 text-sm">File uploads</td>
                                    <td className="px-6 py-4 text-sm text-center text-red-500"><X className="mx-auto h-5 w-5" /></td>
                                    <td className="px-6 py-4 text-sm text-center text-green-600"><Check className="mx-auto h-5 w-5" /></td>
                                </tr>
                                <tr className="bg-gray-50" data-testid='row-price'>
                                    <td className="px-6 py-4 text-sm font-medium">Price</td>
                                    <td className="px-6 py-4 text-sm text-center font-semibold">$0</td>
                                    <td className="px-6 py-4 text-sm text-center font-semibold">$9.99 / mo</td>
                                </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">
                                Selected plan:{' '}
                                <span data-testid='selected-plan' className="font-semibold">{selection ? (selection === SubscriptionType.PREMIUM ? 'Premium' : SubscriptionType.FREE) : 'None'}</span>
                            </p>
                            <button
                                type="submit"
                                disabled={!canSubmit}
                                data-testid='submit-btn'
                                className={`inline-flex items-center justify-center rounded-2xl px-5 py-2.5 text-white shadow-md transition ${
                                    canSubmit
                                        ? 'bg-crumborange hover:brightness-95'
                                        : 'bg-gray-300 cursor-not-allowed'
                                }`}
                            >
                                {submitting ? 'Saving...' : 'Continue'}
                            </button>
                        </div>
                    </form>
                </section>
            </main>
            <Footer />
            <ErrorToast message={errorMessage} clearError={() => setErrorMessage('')} />
        </div>
    );
};

export default SubscriptionSelectPage;